<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import HomePageApiHandler from '@/api/homepage'
import { APP_MODAL_SIZES } from '@/utils/components-configurations/app-modal'
import { handleError } from '@/utils/error-handling'
import { checkResponseStatus, RESPONSE_STATUSES } from '@/utils/response-statuses'
import { useLoadingState } from '@/utils/use-loading-state'

import HomePageAiReportDebugCalls from '@/components/home-page/HomePageAiReportDebugCalls'
import HomePageAiReportDebugSettings from '@/components/home-page/HomePageAiReportDebugSettings'
import {
  HOMEPAGE_AI_REPORT_DEBUG_DIALOG_FORBIDDEN_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_DIALOG_LOADER_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_DIALOG_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppModal from '@/components/ui/AppModal/AppModal'
import LoadingCircle from '@/components/ui/LoadingCircle/LoadingCircle'

defineOptions({
  name: 'HomePageAiReportDebugDialog'
})

/*
 * The debug tools of the Alignment report of a selection (FR-0.6): the settings and the prompts
 * of the person's next generations, the calls and the costs of the last generation.
 */
const props = defineProps({
  selectionName: {
    type: String,
    default: ''
  },

  groupId: {
    type: [String, Number],
    default: null
  },

  accountId: {
    type: [String, Number],
    default: null
  }
})

const show = defineModel('show', {
  type: Boolean
})

const { t } = useI18n()

const DIALOG_ICON_SIZE = 24

const homepageApi = new HomePageApiHandler()

const { isLoading, toggleLoading } = useLoadingState()

const debugSettings = ref(null)

// 403: the user may not generate the Alignment report of the selection (FR-0.5)
const isForbidden = ref(false)

// a read settled after the dialog was opened again must not overwrite the newer one
let lastRequestId = 0

const loadDebugSettings = async () => {
  lastRequestId += 1
  const requestId = lastRequestId

  debugSettings.value = null
  isForbidden.value = false
  toggleLoading(true)

  try {
    const data = await homepageApi.getAiReportDebugSettings({
      groupId: props.groupId,
      accountId: props.accountId
    })

    if (requestId === lastRequestId) {
      debugSettings.value = data
    }
  } catch (error) {
    if (requestId !== lastRequestId) {
      return
    }

    if (checkResponseStatus({ error, status: RESPONSE_STATUSES.FORBIDDEN })) {
      isForbidden.value = true
      return
    }

    handleError({ error })
  } finally {
    if (requestId === lastRequestId) {
      toggleLoading(false)
    }
  }
}

// the settings are read once per open of the dialog
watch(
  show,
  async isShown => {
    if (isShown) {
      await loadDebugSettings()
    }
  },
  { immediate: true }
)

const onClose = () => {
  show.value = false
}
</script>

<template>
  <AppModal
    :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_DIALOG_TEST_ID"
    :data-testid="HOMEPAGE_AI_REPORT_DEBUG_DIALOG_TEST_ID"
    :show="show"
    :size="APP_MODAL_SIZES.XL"
    class="hardd-Dialog"
    close-button-icon="close-next"
    @on-close="onClose"
  >
    <template #title>
      <div class="hardd-Title">
        <AppIcon
          :height="DIALOG_ICON_SIZE"
          :width="DIALOG_ICON_SIZE"
          class="hardd-Title_Icon"
          icon-name="bug"
        />
        <span class="hardd-Title_Text">{{ t('homepage.ai_reports.debug.dialog_title') }}</span>
        <template v-if="selectionName">
          <AppDivider class="hardd-Title_Divider" no-margin vertical />
          <span class="hardd-Title_Selection oboard-truncated-text">
            {{ selectionName }}
          </span>
        </template>
      </div>
    </template>

    <div
      v-if="isLoading"
      :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_DIALOG_LOADER_TEST_ID"
      :data-testid="HOMEPAGE_AI_REPORT_DEBUG_DIALOG_LOADER_TEST_ID"
      class="hardd-Loader"
    >
      <LoadingCircle />
    </div>

    <div
      v-else-if="isForbidden"
      :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_DIALOG_FORBIDDEN_TEST_ID"
      :data-testid="HOMEPAGE_AI_REPORT_DEBUG_DIALOG_FORBIDDEN_TEST_ID"
      class="hardd-Forbidden"
    >
      {{ t('homepage.ai_reports.debug.forbidden') }}
    </div>

    <div v-else-if="debugSettings" class="hardd-Body">
      <HomePageAiReportDebugSettings :debug-settings="debugSettings" />
      <AppDivider no-margin />
      <HomePageAiReportDebugCalls :account-id="accountId" :group-id="groupId" />
    </div>
  </AppModal>
</template>

<style lang="scss" scoped>
.hardd-Dialog {
  font-family: $system-ui;

  // the title bar is closed by a thin line, as in the Alignment report dialog; AppModal reads
  // no CSS variable for its header, so its node is reached the way the other modals do
  &:deep(.o-modal-header) {
    border-bottom: 1px solid $grey-2-next;
  }
}

.hardd-Title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  color: $dark-1;
}

.hardd-Title_Icon {
  flex-shrink: 0;
}

.hardd-Title_Text {
  flex-shrink: 0;
  font-size: $fs-20;
  font-weight: fw('bold');
  line-height: 28px;
}

.hardd-Title_Divider {
  height: 24px;
}

.hardd-Title_Selection {
  color: $dark-2;
  font-size: $fs-20;
  font-weight: fw('regular');
  line-height: 28px;
}

.hardd-Loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.hardd-Forbidden {
  padding: 40px 0;
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
  text-align: center;
}

.hardd-Body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 8px;
}
</style>
